import { LazyImport, LazyModule } from "owa-bundling-light";
import { isGlobalFeatureEnabled } from "owa-feature-flags";
import { getItem as getSessionStorageItem, setItem as setSessionStorageItem, } from "owa-session-storage";
import { RELOAD_MARKER_KEY } from "./constants";
import { setStaleFlightUpdateApplyingMutator } from "./store/mutators";
import { isStaleFlightUpdateApplying, isStaleFlightUpdateAvailable as isStaleFlightUpdateAvailableSelector, } from "./store/selectors";
import reloadPage from "./utils/reloadPage";
import type * as lazyIndex from "./lazyIndex";

const DETECTOR_FLIGHT = "fwk-stale-flight-update-notification";
const IDLE_LOAD_TIMEOUT_MS = 5000;
const INITIALIZATION_RETRY_BASE_MS = 30 * 1000;
const INITIALIZATION_RETRY_MAX_MS = 30 * 60 * 1000;
const INITIALIZATION_RETRY_MAX_EXPONENT = Math.ceil(Math.log2(INITIALIZATION_RETRY_MAX_MS / INITIALIZATION_RETRY_BASE_MS));

const lazyModule: LazyModule<typeof lazyIndex> = new LazyModule(() => import(/* webpackChunkName: "StaleFlightUpdate" */ "./lazyIndex"), { name: "StaleFlightUpdate" });

const lazyInitializeStaleFlightUpdateDetector: LazyImport<typeof lazyIndex.initializeStaleFlightUpdateDetector, typeof lazyModule> = new LazyImport(lazyModule, module => module.initializeStaleFlightUpdateDetector);

const lazyLogStaleFlightUpdateImpression: LazyImport<typeof lazyIndex.logStaleFlightUpdateImpression, typeof lazyModule> = new LazyImport(lazyModule, module => module.logStaleFlightUpdateImpression);

const lazyReloadForStaleFlightUpdate: LazyImport<typeof lazyIndex.reloadForStaleFlightUpdate, typeof lazyModule> = new LazyImport(lazyModule, module => module.reloadForStaleFlightUpdate);

let isInitializationScheduled = false;
let initializationFailureCount = 0;

export function isStaleFlightUpdateNotificationEnabled(): boolean {
    return isGlobalFeatureEnabled(DETECTOR_FLIGHT);
}

export function shouldInitializeStaleFlightUpdateDetector(): boolean {
    return (isStaleFlightUpdateNotificationEnabled() ||
        getSessionStorageItem(self, RELOAD_MARKER_KEY) !== null);
}

export function initializeStaleFlightUpdateDetector(): void {
    if (isInitializationScheduled || !shouldInitializeStaleFlightUpdateDetector()) {
        return;
    }
    
    isInitializationScheduled = true;
    const shouldStartPolling = isStaleFlightUpdateNotificationEnabled();
    
    const initialize = (): void => {
        void lazyInitializeStaleFlightUpdateDetector
            .import()
            .then(loadedInitialize => {
            initializationFailureCount = 0;
            loadedInitialize(shouldStartPolling);
        })
            .catch(() => {
            initializationFailureCount = Math.min(initializationFailureCount + 1, INITIALIZATION_RETRY_MAX_EXPONENT + 1);
            self.setTimeout(initialize, Math.min(INITIALIZATION_RETRY_BASE_MS * 2 ** (initializationFailureCount - 1), INITIALIZATION_RETRY_MAX_MS));
        });
    };
    if (typeof self.requestIdleCallback === "function") {
        self.requestIdleCallback(initialize, { timeout: IDLE_LOAD_TIMEOUT_MS });
    }
    else {
        self.setTimeout(initialize, 0);
    }
}

export function isStaleFlightUpdateAvailable(): boolean {
    return isStaleFlightUpdateAvailableSelector();
}

export { isStaleFlightUpdateApplying };

export function logStaleFlightUpdateImpression(): void {
    void lazyLogStaleFlightUpdateImpression.import().then(logImpression => logImpression(), () => undefined);
}

export function reloadForStaleFlightUpdate(): void {
    if (!isStaleFlightUpdateAvailableSelector() || isStaleFlightUpdateApplying()) {
        return;
    }
    
    setStaleFlightUpdateApplyingMutator(true);
    setSessionStorageItem(self, RELOAD_MARKER_KEY, String(performance.timeOrigin + performance.now()));
    void lazyReloadForStaleFlightUpdate
        .import()
        .then(reload => void reload().catch(() => undefined), reloadPage);
}
